import DOMPurify from 'dompurify';

// 文本类型枚举
export const TEXT_TYPES = {
  PLAIN: 'plain',
  MARKDOWN: 'markdown',
  HTML: 'html',
  JSON: 'json'
};

// 自动检测文本类型
export const detectTextType = (text) => {
  if (!text || typeof text !== 'string') {
    return TEXT_TYPES.PLAIN;
  }

  const trimmedText = text.trim();
  
  // 检测JSON格式
  if ((trimmedText.startsWith('{') && trimmedText.endsWith('}')) ||
      (trimmedText.startsWith('[') && trimmedText.endsWith(']'))) {
    try {
      JSON.parse(trimmedText);
      return TEXT_TYPES.JSON;
    } catch (e) {
      // 不是有效的JSON，继续检测其他格式
    }
  }

  // 检测HTML格式
  const htmlRegex = /<\/?[a-z][\s\S]*>/i;
  if (htmlRegex.test(trimmedText)) {
    return TEXT_TYPES.HTML;
  }

  // 检测Markdown格式
  const markdownPatterns = [
    /^#{1,6}\s+/m,           // 标题
    /\*\*.*?\*\*/,           // 粗体
    /\*.*?\*/,               // 斜体
    /`.*?`/,                 // 行内代码
    /```[\s\S]*?```/,        // 代码块
    /^\s*[*+]\s+/m,          // 无序列表
    /^\s*\d+\.\s+/m,         // 有序列表
    /^\s*>\s+/m,             // 引用
    /\[.*?\]\(.*?\)/,        // 链接
    /!\[.*?\]\(.*?\)/        // 图片
  ];

  if (markdownPatterns.some(pattern => pattern.test(trimmedText))) {
    return TEXT_TYPES.MARKDOWN;
  }

  return TEXT_TYPES.PLAIN;
};

// 清理HTML内容
export const sanitizeHTML = (html) => {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 's', 'del', 'ins', 'mark',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'blockquote', 'pre', 'code',
      'a', 'img',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'div', 'span'
    ],
    ALLOWED_ATTR: [
      'href', 'target', 'rel',
      'src', 'alt', 'title', 'width', 'height',
      'class', 'style'
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i
  });
};

// 格式化JSON显示
export const formatJSON = (jsonString) => {
  try {
    const parsed = JSON.parse(jsonString);
    return JSON.stringify(parsed, null, 2);
  } catch (e) {
    return jsonString;
  }
};

// 转义HTML特殊字符
export const escapeHTML = (text) => {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
};

// 处理纯文本，保留换行和空格
export const formatPlainText = (text) => {
  return text
    .replace(/\n/g, '<br>')
    .replace(/\s{2,}/g, (match) => '&nbsp;'.repeat(match.length));
};

// 提取文本摘要
export const extractSummary = (text, maxLength = 100) => {
  if (!text) return '';
  
  // 移除HTML标签和Markdown语法
  let plainText = text
    .replace(/<[^>]*>/g, '')              // 移除HTML标签
    .replace(/#{1,6}\s+/g, '')            // 移除Markdown标题
    .replace(/\*\*(.*?)\*\*/g, '$1')      // 移除粗体标记
    .replace(/\*(.*?)\*/g, '$1')          // 移除斜体标记
    .replace(/`(.*?)`/g, '$1')            // 移除代码标记
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // 移除链接，保留文本
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1') // 移除图片，保留alt文本
    .replace(/^\s*[-*+>\s]+/gm, '')       // 移除列表和引用标记
    .replace(/\n+/g, ' ')                 // 替换换行为空格
    .trim();

  return plainText.length > maxLength 
    ? plainText.substring(0, maxLength) + '...' 
    : plainText;
};

// 验证文本内容
export const validateText = (text, type = null) => {
  if (!text || typeof text !== 'string') {
    return { isValid: false, error: '内容不能为空' };
  }

  const detectedType = type || detectTextType(text);
  
  switch (detectedType) {
    case TEXT_TYPES.JSON:
      try {
        JSON.parse(text);
        return { isValid: true, type: detectedType };
      } catch (e) {
        return { isValid: false, error: 'JSON格式不正确: ' + e.message };
      }
      
    case TEXT_TYPES.HTML:
      // 基本的HTML验证
      const unclosedTags = text.match(/<([a-z]+)(?:\s[^>]*)?>(?!.*<\/\1>)/gi);
      if (unclosedTags && unclosedTags.length > 0) {
        return { 
          isValid: false, 
          error: `检测到未闭合的HTML标签: ${unclosedTags.join(', ')}`,
          warning: true 
        };
      }
      return { isValid: true, type: detectedType };
      
    case TEXT_TYPES.MARKDOWN:
    case TEXT_TYPES.PLAIN:
    default:
      return { isValid: true, type: detectedType };
  }
};

// 获取文本类型的显示名称
export const getTextTypeName = (type) => {
  const typeNames = {
    [TEXT_TYPES.PLAIN]: '纯文本',
    [TEXT_TYPES.MARKDOWN]: 'Markdown',
    [TEXT_TYPES.HTML]: 'HTML',
    [TEXT_TYPES.JSON]: 'JSON'
  };
  return typeNames[type] || '未知格式';
};

const textParserUtils = {
  TEXT_TYPES,
  detectTextType,
  sanitizeHTML,
  formatJSON,
  escapeHTML,
  formatPlainText,
  extractSummary,
  validateText,
  getTextTypeName
};

export default textParserUtils; 